import { sb } from '../supabase';
import { creditOf } from './growthAds';

// Growth OS «الربحية» — owner, 2026-11-01. Profit per paid market order, the
// 299 request fees, campaign profit and break-even. Every cost component says
// whether it is «فعلي» (recorded) or «تقديري» (assumed) and where it comes
// from; nothing is filled in silently. Assumptions live in
// growth_profit_settings, editable on the page.
//
// The rules the owner set:
//   • card class from the Telr response: MADA → مدى, card country SA → local
//     credit, else international; unknown (and Tabby/Tamara until one is
//     seen) → local credit, «تقديري». VAT 15% is added on every Telr fee.
//   • yuan → SAR: the settings rate, «تقديري», until a real rate is stored.
//   • the 299 request fees count as earned (also those marked refund_due).
//   • MK-261021-00003 and team accounts' orders are left out (server-side).

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (v) => (v == null || v === '' ? null : Number(v));
const fmt = (n, d = 2) => Number(n).toLocaleString('en-US', { maximumFractionDigits: d });

export const CARD_AR = { mada: 'مدى', local_credit: 'بطاقة ائتمان محلية', international: 'بطاقة دولية', unknown: 'غير معروف' };
const FEE_KEY = { mada: 'telr_mada', local_credit: 'telr_local_credit', international: 'telr_international' };

// rows of growth_profit_settings → what the calculations read.
export function readSettings(rows = []) {
  const on = rows.filter((r) => r.active !== false);
  const by = Object.fromEntries(on.map((r) => [r.key, r]));
  const vat = num(by.vat?.value) ?? 15;
  const fixed = on.filter((r) => r.kind === 'fixed_monthly').sort((a, b) => a.sort - b.sort);
  const monthlyFixed = r2(fixed.reduce((t, r) => t + (Number(r.amount_sar) || 0) * (r.vat_applies ? 1 + vat / 100 : 1), 0));
  const ship = on.filter((r) => r.kind === 'shipping');
  return {
    rows: on, vat,
    fees: Object.fromEntries(Object.entries(FEE_KEY).map(([cls, key]) => [cls, by[key] || null])),
    fx: by.fx_cny_sar || null,
    fixed, monthlyFixed,
    shipping: ship.find((r) => r.is_default) || ship[0] || null,
    hiobuyPct: num(by.hiobuy_service_fee?.pct),
    excluded: on.filter((r) => r.kind === 'exclude_order').map((r) => r.label_ar),
  };
}

// One Telr charge: (amount × % + fixed) + VAT on the fee.
export function telrFee(amount, cls, s) {
  const known = cls && cls !== 'unknown' && s.fees[cls];
  const use = known ? cls : 'local_credit';
  const f = s.fees[use];
  if (!f) return { sar: null, actual: false, source: 'لا توجد رسوم Telr في الإعدادات' };
  const base = (Number(amount) || 0) * (Number(f.pct) || 0) / 100 + (Number(f.fixed_sar) || 0);
  const sar = r2(base * (1 + s.vat / 100));
  const rate = `${fmt(f.pct)}%${Number(f.fixed_sar) ? ` + ${fmt(f.fixed_sar)} ر.س` : ''} + ضريبة ${fmt(s.vat, 0)}%`;
  return known
    ? { sar, actual: true, source: `${CARD_AR[use]} (من رد Telr): ${rate}` }
    : { sar, actual: false, source: `نوع البطاقة غير معروف — حُسبت كبطاقة محلية: ${rate}` };
}

const fxRate = (s) => num(s.fx?.value);
const cnyToSar = (cny, s) => (fxRate(s) == null || cny == null ? null : r2(cny * fxRate(s)));
const fxNote = (s) => (fxRate(s) == null ? 'لا يوجد سعر صرف في الإعدادات' : `سعر الصرف ${fmt(fxRate(s), 4)} ر.س لليوان (تقديري، من الإعدادات)`);

// Profit of one paid market order: each component labelled actual/estimate.
export function orderProfit(o, s) {
  const paid = num(o.paid_sar) ?? num(o.total_sar) ?? 0;
  const comps = [];
  const add = (key, ar, sar, actual, source) => comps.push({ key, ar, sar: sar == null ? null : r2(sar), actual, source });

  // What HioBuy charges, in three parts: goods, shipping inside China, and its
  // service fee. Each from the most certain source recorded for the order.
  // The amounts in yuan can be actual; in SAR they stay «تقديري» while the
  // yuan rate comes from the settings (owner, 2026-11-01).
  const recorded = !!o.costs_recorded_at;
  const hbGoods = num(o.hb_goods_cny); const hbShip = num(o.hb_shipping_cny); const hbTotal = num(o.hb_total_cny);
  const yuan = (cny, what) => `${what} ${fmt(cny)} ¥ · ${fxNote(s)}`;

  let goods;
  if (recorded && num(o.actual_goods_cny) != null) { goods = num(o.actual_goods_cny); add('product', 'البضاعة (HioBuy)', cnyToSar(goods, s), false, yuan(goods, 'المبلغ الفعلي المسجّل')); }
  else if (hbGoods != null) { goods = hbGoods; add('product', 'البضاعة (HioBuy)', cnyToSar(goods, s), false, yuan(goods, 'قيمة البضاعة في طلب HioBuy')); }
  else { goods = num(o.goods_cny) || 0; add('product', 'البضاعة (HioBuy)', cnyToSar(goods, s), false, yuan(goods, 'سعر المصنع عند الطلب (لم يُسجّل المدفوع فعلاً)')); }

  let domestic;
  if (recorded && num(o.actual_domestic_cny) != null) { domestic = num(o.actual_domestic_cny); add('domestic', 'الشحن داخل الصين', cnyToSar(domestic, s), false, yuan(domestic, 'المبلغ الفعلي المسجّل')); }
  else if (hbShip != null) { domestic = hbShip; add('domestic', 'الشحن داخل الصين', cnyToSar(domestic, s), false, yuan(domestic, 'شحن طلب HioBuy داخل الصين')); }
  else if (num(o.domestic_cny) != null) { domestic = num(o.domestic_cny); add('domestic', 'الشحن داخل الصين', cnyToSar(domestic, s), false, yuan(domestic, 'تقدير الطلب عند الدفع')); }
  else { domestic = 0; add('domestic', 'الشحن داخل الصين', null, false, 'غير مسجّل ولا مقدّر لهذا الطلب'); }

  if (hbTotal != null) {
    const fee = Math.max(0, r2(hbTotal - (hbGoods ?? goods) - (hbShip ?? domestic)));
    add('hiobuy_fee', 'رسوم خدمة HioBuy', cnyToSar(fee, s), false, yuan(fee, `إجمالي طلب HioBuy ${fmt(hbTotal)} ¥ ناقص البضاعة والشحن =`));
  } else if (num(o.procure_amount_cny) != null) {
    const fee = Math.max(0, r2(num(o.procure_amount_cny) - goods - domestic));
    add('hiobuy_fee', 'رسوم خدمة HioBuy', cnyToSar(fee, s), false, yuan(fee, `المدفوع لـHioBuy ${fmt(o.procure_amount_cny)} ¥ ناقص البضاعة والشحن =`));
  } else if (s.hiobuyPct != null) {
    const fee = r2((goods * s.hiobuyPct) / 100);
    add('hiobuy_fee', 'رسوم خدمة HioBuy', cnyToSar(fee, s), false, yuan(fee, `${fmt(s.hiobuyPct)}% من البضاعة (من الإعدادات) =`));
  } else {
    add('hiobuy_fee', 'رسوم خدمة HioBuy', null, false, 'لا توجد نسبة رسوم HioBuy في الإعدادات');
  }

  // Optional services the trader bought (inspection, photos…), when any.
  if (recorded && num(o.actual_services_cny)) add('services', 'خدمات إضافية', cnyToSar(num(o.actual_services_cny), s), false, yuan(num(o.actual_services_cny), 'المبلغ الفعلي المسجّل'));
  else if (num(o.services_sar)) add('services', 'خدمات إضافية', num(o.services_sar), false, 'يُفترض أنها تكلّف ما حُصّل من العميل');

  // International shipping.
  if (num(o.actual_freight_sar) != null) {
    // Not an invoice: the warehouse's measured weight and size × the store's freight rates.
    add('shipping', 'الشحن الدولي', num(o.actual_freight_sar), false, 'الوزن والأبعاد الفعلية من المستودع × سعر الشحن في إعدادات المتجر');
  } else if (num(o.actual_intl_cny) != null) {
    add('shipping', 'الشحن الدولي', cnyToSar(num(o.actual_intl_cny), s), false, `المبلغ الفعلي ${fmt(o.actual_intl_cny)} ¥ · ${fxNote(s)}`);
  } else {
    const ag = s.shipping;
    const sea = o.shipping_method === 'sea';
    const kg = num(o.chargeable_kg); const cbm = num(o.est_cbm);
    if (ag && sea && cbm != null && num(ag.rate_sea_cbm) != null) {
      add('shipping', 'الشحن الدولي', cbm * num(ag.rate_sea_cbm), false, `${ag.label_ar}: ${fmt(ag.rate_sea_cbm, 0)} ر.س/م³ × ${fmt(cbm, 4)} م³ (من الإعدادات)`);
    } else if (ag && !sea && kg != null && num(ag.rate_air_kg) != null) {
      add('shipping', 'الشحن الدولي', kg * num(ag.rate_air_kg), false, `${ag.label_ar}: ${fmt(ag.rate_air_kg, 0)} ر.س/كجم × ${fmt(kg, 3)} كجم (من الإعدادات)`);
    } else {
      add('shipping', 'الشحن الدولي', num(o.intl_estimate_sar), false, 'تقدير الطلب عند الدفع (لا توجد أسعار شحن مطابقة في الإعدادات)');
    }
  }

  // Customs and import charges, collected from the trader and paid on.
  if (num(o.import_sar)) add('import', 'رسوم الاستيراد والجمارك', num(o.import_sar), false, 'يُفترض أنها تُدفع كما حُصّلت من العميل');

  // Telr's fee on the payment.
  const fee = telrFee(paid, o.card_class, s);
  add('payment', 'رسوم الدفع (Telr)', fee.sar, fee.actual, fee.source);

  // Refunds.
  if (num(o.refunds_sar)) add('refund', 'الاسترداد', num(o.refunds_sar), true, 'من سجل الاسترداد');

  const cost = comps.reduce((t, c) => t + (c.sar || 0), 0);
  return {
    id: o.id, order_no: o.order_no, paid_at: o.paid_at, card_class: o.card_class,
    revenue: r2(paid), components: comps, cost: r2(cost), net: r2(paid - cost),
    estimates: comps.filter((c) => !c.actual).length, missing: comps.some((c) => c.sar == null),
  };
}

// The 299 request fee: earned (owner, 2026-11-01: the refund_due marks are stale).
export function feeProfit(f, s) {
  const amount = Number(f.amount) || 0;
  const fee = telrFee(amount, 'unknown', s);
  const refunded = f.refunded_at ? amount : 0;
  return { id: f.id, paid_at: f.paid_at, revenue: amount, telr: fee, refunded, net: r2(amount - (fee.sar || 0) - refunded) };
}

const DAY = 864e5;
// Everything for a period [start, end).
export function summarizeProfit(data, s, win) {
  const orders = (data?.orders || []).map((o) => orderProfit(o, s));
  const fees = (data?.fees || []).map((f) => feeProfit(f, s));
  const byKey = {};
  orders.forEach((o) => o.components.forEach((c) => {
    const k = byKey[c.key] || (byKey[c.key] = { key: c.key, ar: c.ar, sar: 0, actual: 0, estimate: 0 });
    k.sar = r2(k.sar + (c.sar || 0));
    if (c.actual) k.actual += 1; else k.estimate += 1;
  }));
  const revenue = r2(orders.reduce((t, o) => t + o.revenue, 0));
  const ordersNet = r2(orders.reduce((t, o) => t + o.net, 0));
  const feesNet = r2(fees.reduce((t, f) => t + f.net, 0));
  const days = Math.max(1, Math.round((win.end - win.start) / DAY));
  const fixedShare = r2((s.monthlyFixed * days) / 30);
  const avg = orders.length ? r2(ordersNet / orders.length) : null;
  const aov = orders.length ? r2(revenue / orders.length) : null;
  return {
    orders, fees, revenue, ordersNet, feesNet, feeRevenue: r2(fees.reduce((t, f) => t + f.revenue, 0)),
    breakdown: ['product', 'domestic', 'hiobuy_fee', 'services', 'shipping', 'import', 'payment', 'refund'].map((k) => byKey[k]).filter(Boolean),
    net: r2(ordersNet + feesNet), avg, aov, fixedShare, days, afterFixed: r2(ordersNet + feesNet - fixedShare),
  };
}

// Orders needed in a month to cover the fixed monthly costs.
export function breakEven(monthlyFixed, avgNet) {
  if (avgNet == null) return { needed: null, why: 'لا توجد طلبات مدفوعة في الفترة لحساب متوسط الربح' };
  if (avgNet <= 0) return { needed: null, why: 'متوسط ربح الطلب صفر أو خسارة — لا يصل عدد الطلبات وحده إلى التعادل' };
  return { needed: Math.ceil(monthlyFixed / avgNet), why: null };
}

// Per campaign: spend, paid orders credited to it, their net profit, after ads.
export function campaignProfit({ orders, attribution, daily, campaigns }, touch = 'last_touch') {
  const rows = {};
  const key = (pl, id) => `${pl}|${id || ''}`;
  const row = (pl, id) => rows[key(pl, id)] || (rows[key(pl, id)] = { platform: pl, campaign_id: id || null, name: null, spend: 0, orders: 0, net: 0 });
  (campaigns || []).forEach((c) => { row(c.platform, c.campaign_id).name = c.name; });
  (daily || []).forEach((d) => { row(d.platform, d.campaign_id).spend += Number(d.spend) || 0; });
  const net = Object.fromEntries((orders || []).map((o) => [o.id, o.net]));
  const credited = new Set();
  (attribution || []).forEach((a) => {
    if (!(a.subject_id in net) || credited.has(a.subject_id)) return;
    const c = creditOf(a[touch] || {});
    if (!c) return;
    credited.add(a.subject_id);
    const r = row(c.platform, c.campaign_id);
    r.orders += 1; r.net += net[a.subject_id];
  });
  const other = { platform: null, campaign_id: null, name: 'بلا حملة (مباشر / بحث / غير معروف)', spend: 0, orders: 0, net: 0 };
  (orders || []).forEach((o) => { if (!credited.has(o.id)) { other.orders += 1; other.net += o.net; } });
  const list = Object.values(rows).filter((r) => r.spend > 0 || r.orders > 0).map((r) => ({
    ...r, spend: r2(r.spend), net: r2(r.net), afterAds: r2(r.net - r.spend), perOrder: r.orders ? r2(r.spend / r.orders) : null,
  })).sort((a, b) => b.spend - a.spend || b.orders - a.orders);
  if (other.orders) list.push({ ...other, net: r2(other.net), afterAds: r2(other.net), perOrder: null });
  return list;
}

// ── data ─────────────────────────────────────────────────────────────────────
export async function fetchProfitSettings() {
  const { data, error } = await sb.from('growth_profit_settings').select('*').order('sort');
  if (error) throw error;
  return data || [];
}
export async function saveProfitSetting(row) {
  const { error } = await sb.from('growth_profit_settings').upsert({ ...row, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}
export async function removeProfitSetting(key) {
  const { error } = await sb.from('growth_profit_settings').delete().eq('key', key);
  if (error) throw error;
}
export async function fetchProfitData(start, end) {
  const { data, error } = await sb.rpc('growth_profit_data', { p_from: new Date(start).toISOString(), p_to: new Date(end).toISOString() });
  if (error) throw error;
  return data || { orders: [], fees: [] };
}
export async function fetchCampaignInputs(orderIds, range) {
  const [attr, daily, camps] = await Promise.all([
    orderIds.length ? sb.from('growth_attribution').select('subject_id, first_touch, last_touch').eq('kind', 'order').in('subject_id', orderIds) : Promise.resolve({ data: [] }),
    sb.from('growth_ad_daily').select('platform, campaign_id, spend').gte('day', range.from).lte('day', range.to).limit(20000),
    sb.from('growth_ad_campaigns').select('platform, campaign_id, name'),
  ]);
  for (const r of [attr, daily, camps]) if (r.error) throw r.error;
  return { attribution: attr.data || [], daily: daily.data || [], campaigns: camps.data || [] };
}
